import styled from '@emotion/styled';
import { Box, Button, DialogActions, FormHelperText, Grid, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
    ContactInfo,
    ContactSection,
    EditRequestDTO,
    EditTextContentDTO,
    HeroSection,
    Section,
    SectionType,
} from '../../../../api/models';
import { useEffect, useState } from 'react';
import { AddressSeemsInvalidError, NumberInvalidError, WebsiteLinkInvalidError } from '../../../../api/error';
import { useEditForm } from '../../../../api';

const BackgroundBoxWrapper = styled(Box)<{ backgroundColor: string }>(({ backgroundColor }) => ({
    // paddingTop: 32,
    paddingLeft: 54,
    paddingRight: 54,
    paddingBottom: 32,
    margin: 0,
    backgroundColor: backgroundColor,
}));

interface Props {
    section: Section;
    websiteId: string;
    throwSnackbarMessage: (message: string) => void;
    refetch: VoidFunction;
}

export const UpdateContactInfoComponent = (props: Props) => {
    const { section, throwSnackbarMessage } = props;
    let contactInfo: ContactInfo | undefined = undefined;
    const [addressString, setAddressString] = useState<string | undefined>(undefined);
    const [contactNumber, setContactNumber] = useState<string | undefined>(undefined);
    const [whatsappNumber, setWhatsappNumber] = useState<string | undefined>(undefined);
    const [googleMapsLink, setGoogleMapsLink] = useState<string | undefined>(undefined);
    const [facebookLink, setFacebookLink] = useState<string | undefined>(undefined);
    const [instagramLink, setInstagramLink] = useState<string | undefined>(undefined);
    const [edits, updateEdits] = useState<EditTextContentDTO[]>([]);
    const theme = useTheme();

    const { editWebsiteInfo } = useEditForm({
        websiteId: props.websiteId,
    });

    const handleUpdates = (newTextValue: string, textComponentId: string) => {
        const updateContentRequestDTO = {
            id: textComponentId,
            text: newTextValue,
        };
        if (edits.length === 0) {
            updateEdits([updateContentRequestDTO]);
        } else {
            const currentEdits = edits.filter((component) => component.id !== textComponentId);
            currentEdits.push(updateContentRequestDTO);
            updateEdits(currentEdits);
        }
    };

    const resetState = () => {
        setAddressString('');
        setContactNumber('');
        setWhatsappNumber('');
        setGoogleMapsLink('');
        setFacebookLink('');
        setInstagramLink('');
    };

    useEffect(() => {
        if (contactInfo) {
            setAddressString(contactInfo.addressString.text);
            setContactNumber(contactInfo.contactNumber.text);
            setWhatsappNumber(contactInfo.whatsappNumber.text);
            setGoogleMapsLink(contactInfo.googleMapsLink.text);
            setFacebookLink(contactInfo.facebookLink.text);
            setInstagramLink(contactInfo.instagramLink.text);
        } else {
            resetState();
        }
    }, [contactInfo]);

    const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const buttonStyles = isMobileScreen
        ? {
              padding: '10px 20px',
              fontSize: '14px',
              borderRadius: '8px',
              width: '100%',
              backgroundColor: '#90909d',
              color: 'white',
              overflow: 'hidden',
              textAlign: 'center',
              whitespace: 'normal',
              lineHeight: '1.2',
          }
        : {};

    const validateNumber = (mobileNum: string | undefined, typeName: string) => {
        if (mobileNum) {
            const regexp = /^\+\d{1,4}-\d{7,15}$/;
            if (!regexp.test(mobileNum)) {
                throw new NumberInvalidError(`Please use the format for ${typeName}: +<country code>-<local number> (e.g., +1-1234567890).`);
            } else {
                return;
            }
        } else {
            throw new NumberInvalidError('Please enter the mobile number');
        }
    };

    const validateUrl = (url: string | undefined, typeName: string) => {
        if (url) {
            try {
                new URL(url);
            } catch (err) {
                throw new WebsiteLinkInvalidError(`Website URL is not in a valid format for ${typeName}, please check`);
            }
        } else {
            // we can just ignore this flow
        }
    };

    const validateAddress = (addressString: string | undefined) => {
        if (addressString) {
            if (addressString.length > 20) {
                return;
            } else {
                throw new AddressSeemsInvalidError(
                    'Please add address exhaustively for users to be able to find your place easily and address should be at least 20 characters long',
                );
            }
        }
    };

    const validateInfo = (): boolean => {
        try {
            validateNumber(contactNumber, 'mobile number');
            validateNumber(whatsappNumber, 'whatsapp number');
            validateUrl(googleMapsLink, 'google maps');
            validateUrl(facebookLink, 'facebook');
            validateUrl(instagramLink, 'instagram');
            validateAddress(addressString);
            return true;
        } catch (error) {
            if (error instanceof NumberInvalidError ||
                error instanceof WebsiteLinkInvalidError ||
                error instanceof AddressSeemsInvalidError) {
                throwSnackbarMessage(error.message);
            } else {
                console.info(error);
            }
            return false;
        }
    };

    const updateContactInfo = () => {
        const isValid = validateInfo();
        if (isValid) {
            const updateStructure: EditRequestDTO = {
                updates: edits,
            };
            editWebsiteInfo({
                data: {
                    editRequestDTO: updateStructure,
                    cb: props.refetch,
                },
                throwSnackbarMessage: (errorMessage: string) => throwSnackbarMessage(errorMessage),
            });
            updateEdits([]);
        } else {
            console.info('Ignore this flow');
        }
    };

    useEffect(() => {
        if (contactInfo) {
            const {
                addressString,
                contactNumber,
                whatsappNumber,
                googleMapsLink,
                facebookLink,
                instagramLink,
            } = contactInfo;
            setAddressString(addressString.text);
            setContactNumber(contactNumber.text);
            setWhatsappNumber(whatsappNumber.text);
            setGoogleMapsLink(googleMapsLink.text);
            setFacebookLink(facebookLink.text);
            setInstagramLink(instagramLink.text);
        }
    }, [contactInfo]);

    switch (section?.sectionType) {
        case SectionType.HERO:
            const heroSection = section as HeroSection;
            contactInfo = heroSection.contactInfo;
            break;
        case SectionType.CONTACT:
            const contactSection = section as ContactSection;
            contactInfo = contactSection.contactInfo;
            break;
        default:
            break;
    }
    if (contactInfo) {
        return (
            <div>
                <BackgroundBoxWrapper backgroundColor={'white'}>
                    <Grid container>
                        <Grid item md={12} xs={12} sm={12}>
                            <Typography
                                sx={{
                                    marginTop: 1,
                                    fontWeight: 700,
                                    color: 'black',
                                    fontFamily: 'Poppins',
                                }}
                                variant='body1'>
                                Update Contact Information
                            </Typography>
                        </Grid>
                    </Grid>
                    <br />
                    <Grid style={{marginTop: 12}} container>
                        <Grid item md={12} xs={12} sm={12}>
                            <TextField
                                style={{
                                    color: 'black',
                                }}
                                id={contactInfo.contactNumber.id}
                                label='Contact Number'
                                variant='outlined'
                                fullWidth
                                value={contactNumber}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setContactNumber(e.target.value);
                                    handleUpdates(e.target.value, contactInfo.contactNumber.id);
                                }}
                            />
                            <FormHelperText id='about-the-number'>
                                Please make sure the number follows a format like +1-1234567890 if 1234567890 is the
                                mobile number and +1 is the country code
                            </FormHelperText>
                        </Grid>
                    </Grid>

                    <Grid style={{marginTop: 12}} container>
                        <Grid item md={12} xs={12} sm={12}>
                            <TextField
                                style={{
                                    color: 'black',
                                }}
                                id={contactInfo.addressString.id}
                                label='Address'
                                variant='outlined'
                                fullWidth
                                value={addressString}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setAddressString(e.target.value);
                                    handleUpdates(e.target.value, contactInfo.addressString.id);
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid style={{marginTop: 12}} container>
                        <Grid item md={12} xs={12} sm={12}>
                            <TextField
                                style={{
                                    color: 'black',
                                }}
                                id={contactInfo.whatsappNumber.id}
                                label='Whatsapp Number'
                                variant='outlined'
                                fullWidth
                                value={whatsappNumber}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setWhatsappNumber(e.target.value);
                                    handleUpdates(e.target.value, contactInfo.whatsappNumber.id);
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid style={{marginTop: 12}} container>
                        <Grid item md={12} xs={12} sm={12}>
                            <TextField
                                style={{
                                    color: 'black',
                                }}
                                id={contactInfo.googleMapsLink.id}
                                label='Google Maps Link'
                                variant='outlined'
                                fullWidth
                                value={googleMapsLink}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setGoogleMapsLink(e.target.value);
                                    handleUpdates(e.target.value, contactInfo.googleMapsLink.id);
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Grid style={{marginTop: 12}} container>
                        <Grid item md={12} xs={12} sm={12}>
                            <TextField
                                style={{
                                    color: 'black',
                                }}
                                id={contactInfo.facebookLink.id}
                                label='Facebook'
                                variant='outlined'
                                fullWidth
                                value={facebookLink}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setFacebookLink(e.target.value);
                                    handleUpdates(e.target.value, contactInfo.facebookLink.id);
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid style={{marginTop: 12}} container>
                        <Grid item md={12} xs={12} sm={12}>
                            <TextField
                                style={{
                                    color: 'black',
                                }}
                                id={contactInfo.instagramLink.id}
                                label='Instagram Link'
                                variant='outlined'
                                fullWidth
                                value={instagramLink}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setInstagramLink(e.target.value);
                                    handleUpdates(e.target.value, contactInfo.instagramLink.id);
                                }}
                            />
                        </Grid>
                    </Grid>
                    <br />
                    <Grid justifyContent={'center'} container>
                        <Grid>
                            <DialogActions sx={{ gap: isMobileScreen ? 2 : 1}}>
                                <Button sx={buttonStyles} onClick={updateContactInfo}>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                            color: isMobileScreen ? 'white' : '',
                                            fontFamily: 'Poppins',
                                            whiteSpace: 'nowrap',
                                        }}
                                        variant='body1'>
                                        Save Changes
                                    </Typography>
                                </Button>
                                <Button sx={buttonStyles} onClick={resetState}>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                            color: isMobileScreen ? 'white' : '',
                                            fontFamily: 'Poppins',
                                        }}
                                        variant='body1'>
                                        Dismiss
                                    </Typography>
                                </Button>
                            </DialogActions>
                        </Grid>
                    </Grid>
                </BackgroundBoxWrapper>
            </div>
        );
    } else {
        return <></>;
    }
};
